// --------------------------------------------------------------
// Created On: 2026-03-23
// Author: Dimitra Weinstein
//
// Last Modified: 2026-04-14
// Modified By: Dimitra Weinstein
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import { setCurrentUser } from "src/redux/features/currentUserSlice";
import { CROSS_SUBSIDIARY_PERMISSION, RELEASE_SUBSIDIARY_PERMISSION } from "src/constants/permissions";
import userHasPermission from "src/utilities/userHasPermission";
import useReduxSelector from "src/hooks/useReduxSelector";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import Modal from "src/components/Modal/Modal";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import DoubleConfirmModal from "src/components/DoubleConfirmModal/DoubleConfirmModal";
import styles from "./ManageSubsidiaryModal.module.scss";
import { MAX_SUBSIDIARY_NAME_LENGTH } from "src/constants/miscellaneous";
import SaveChangesModal from "src/components/SaveChangesModal/SaveChangesModal";

export default function ManageSubsidiaryModal(props: Props): Component {
  const [nameChanged, setNameChanged] = useState<boolean>(false);
  const [showConfirmChanges, setShowConfirmChanges] = useState<boolean>(false);
  const [newCompanyName, setNewCompanyName] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  function stepIntoSubsidiary(subsidiaryCompanyId: number, subsidiaryCompanyName: string): void {
    dispatch(
      setCurrentUser({
        userId: currentUser.userId,
        userName: currentUser.userName,
        emailAddress: currentUser.emailAddress,
        originalCompanyId: currentUser.originalCompanyId,
        originalCompanyName: currentUser.originalCompanyName,
        companyId: currentUser.companyId,
        companyName: currentUser.companyName,
        subsidiaryCompanyId: subsidiaryCompanyId,
        subsidiaryCompanyName: subsidiaryCompanyName,
        isCompanyHidden: false,
        permissions: currentUser.permissions,
        isPackager: currentUser.isPackager,
        mfaEnabled: currentUser.mfaEnabled,
        isInSubsidiary: true,
      })
    );
    navigate("/dashboard/map", { replace: true });
  }

  function releaseCompany(subsidiaryCompanyId: number): void {
    props.onReleaseCompany(subsidiaryCompanyId);
  }

  function closeReleaseSubsidiaryModal(): void {
    props.onShowReleaseModal(false);
  }

  function closeManageSubsidiaryModal(): void {
    setNameChanged(false);
    setNewCompanyName("");
    props.onClose();
  }

  async function saveChanges(): Promise<void> {
    // Api call needs to be added to actually update company name.
  }

  function discardChanges(): void {
    setNameChanged(false);
    setNewCompanyName("");
    setShowConfirmChanges(false);
  }

  return (
    <Fragment>
      <Modal
        show={props.showModal}
        onHide={() => {
          //   do nothing
        }}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">Manage Subsidiary Company</h5>
        </ModalHeader>
        <ModalBody>
          <div className="row px-4">
            <input
              data-test="subsidiary-name-input"
              className="form-control mb-3 px-2 mx-auto fs-5 fw-medium"
              type="text"
              maxLength={MAX_SUBSIDIARY_NAME_LENGTH}
              value={
                props.subsidiaryCompanyName && nameChanged === false ? props.subsidiaryCompanyName : newCompanyName
              }
              onChange={(e) => {
                setNameChanged(true);
                setNewCompanyName(e.target.value);
                // Api call needs to be added to actually update company name.
              }}
            />
            <div className="col-12 px-4 align-items-center">
              <div className="row px-4 mb-3 justify-content-center align-items-center bg-primary bg-opacity-25 rounded">
                <p className="col-8 px-4 pt-2 fw-medium">
                  Enter into the subsidiary company and retain all permissions that you have in your current company.
                </p>
                <div className="col-4 px-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!userHasPermission([[CROSS_SUBSIDIARY_PERMISSION]])}
                    onClick={() => stepIntoSubsidiary(props.subsidiaryCompanyId, props.subsidiaryCompanyName)}
                  >
                    Step Into Subsidiary
                  </button>
                </div>
              </div>

              <div className="row px-4 mb-3 justify-content-center align-items-center bg-primary bg-opacity-25 rounded">
                <p className="col-8 px-4 pt-2 fw-medium">
                  Release the subsidiary company. The company will be free to rent assets from any company and you will
                  lose access to enter it. This action is irrevocable.
                </p>
                <div className="col-4 px-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => props.onShowReleaseModal(true)}
                    disabled={!userHasPermission([[RELEASE_SUBSIDIARY_PERMISSION]])}
                  >
                    Release Subsidiary
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="row">
            {nameChanged && (
              <div className="col-auto">
                <button type="button" className="btn btn-success" onClick={() => setShowConfirmChanges(true)}>
                  Save Changes
                </button>
              </div>
            )}
            <div className="col-auto px-auto">
              <button type="button" className="btn btn-primary" onClick={() => closeManageSubsidiaryModal()}>
                Cancel
              </button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <DoubleConfirmModal
        showModal={props.showReleaseModal}
        title="Confirm Release of Subsidiary Company"
        content="Are you sure that you want to release this subsidiary company? If you release this company, you will no
            longer be able to step into it, and the company will be free to rent assets from any other company in RPM?"
        confirmPin="Release Company"
        confirmText="Release Company"
        errorMessage={props.errorMessage}
        onConfirm={() => {
          releaseCompany(props.subsidiaryCompanyId);
        }}
        onCancel={() => closeReleaseSubsidiaryModal()}
      />

      <SaveChangesModal
        showModal={showConfirmChanges}
        title="Changes have not been saved!"
        content="Are you sure that you want to exit without saving your changes?"
        onClose={() => setShowConfirmChanges(false)}
        onSave={() => void saveChanges()}
        onNoSave={() => discardChanges()}
      />
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  showReleaseModal: boolean;
  subsidiaryCompanyId: number;
  subsidiaryCompanyName: string;
  errorMessage: string;
  onReleaseCompany: (subsidiaryCompanyId: number) => void;
  onShowReleaseModal: (show: boolean) => void;
  onClose: () => void;
}
